/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Container,
  Stack,
  Title,
  Text,
  Grid,
  Card,
  Group,
  Badge,
  Box,
  LoadingOverlay,
  Flex,
  Button,
  Paper,
  Radio,
  Textarea,
  Alert,
  Tabs,
  Avatar,
  Tooltip,
  ScrollArea,
  Select,
  Center,
  List,
  Modal
} from '@mantine/core';
import {
  IconCalendar,
  IconClock,
  IconCheck,
  IconAlertCircle,
  IconUser,
  IconDeviceFloppy,
  IconSend,
  IconUsers,
  IconFilter,
  IconCircleCheck
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getAssessmentAvailabilityStatus } from 'utils/assessmentStatus';

dayjs.extend(utc);
dayjs.extend(timezone);
import { assessmentScheduleService } from 'services/lia/assessmentScheduleService';
import NotFoundPage from 'pages/NotFound';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import { decodeShareToken } from 'utils/shareToken';
import EmailForm from './EmailForm';

const EXTERNAL_EMAIL_KEY = 'external_assessment_email';
const DRAFT_STORAGE_KEY = 'external_assessment_draft_';

interface RangeItem {
  trAssessmentQuestionRangeItemID: string;
  label: string;
  value: string;
  sequence: number;
}

interface Question {
  trAssessmentQuestionID: string;
  title: string;
  description?: string;
  type: string;
  moduleId?: string;
  respondentType?: string;
  rangeItems?: RangeItem[];
  typeLabel?: string;
}

interface Module {
  moduleID: string;
  moduleName?: string;
  trAssessmentModuleID?: string;
  questions?: Question[];
}

interface Participant {
  binusianId: string;
  binusianID?: string;
  binusianName?: string;
  name: string;
  email?: string;
  type?: string;
  division?: string;
  trAssessmentParticipantID?: string;
}

const ExternalAssessmentDetailPage: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const encryptedId = searchParams.get('e') || '';
  const tokenParam = searchParams.get('tk') || '';
  const beFromUrl = searchParams.get('be') || '';

  const [email, setEmail] = useState<string>('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [responses, setResponses] = useState<
    Record<string, Record<string, Record<string, string>>>
  >({});
  const [formError, setFormError] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [activeParticipant, setActiveParticipant] = useState<string | null>(null);
  const [_completedModules, setCompletedModules] = useState<string[]>([]);
  const [draftSaved, setDraftSaved] = useState(false);
  const [selectedQuestionType, setSelectedQuestionType] = useState<string | null>(null);
  const [lockedQuestionTypeFromUrl, setLockedQuestionTypeFromUrl] = useState<string | null>(null);
  const [availableQuestionTypes, setAvailableQuestionTypes] = useState<string[]>([]);
  const questionSectionRef = useRef<HTMLDivElement>(null);
  const [questionCompletionStatus, setQuestionCompletionStatus] = useState<
    Record<string, Record<string, Record<string, boolean>>>
  >({});
  const [lockedParticipantSelection, setLockedParticipantSelection] = useState(false);
  const [participantIdFromToken, setParticipantIdFromToken] = useState<string | null>(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [unassessedParticipants, setUnassessedParticipants] = useState<Participant[]>([]);

  const {
    data: assessment,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['externalAssessmentDetail', encryptedId],
    queryFn: () => assessmentScheduleService.getExternalAssessmentDetail(encryptedId),
    enabled: !!encryptedId,
    refetchOnWindowFocus: false
  });

  // Derive list of available external question type labels when assessment loads / updates
  useEffect(() => {
    if (!assessment?.modules) return;
    const allExternalQuestions: Question[] = [];
    assessment.modules.forEach((module: { questions?: Question[] }) => {
      if (!module.questions) return;
      const externalQuestions = module.questions.filter(
        (q) =>
          q.respondentType?.toLowerCase() === 'external' ||
          q.title.toLowerCase().includes('external')
      );
      if (externalQuestions.length) allExternalQuestions.push(...externalQuestions);
    });
    const typeLabels = allExternalQuestions
      .map((q) => q.typeLabel || 'General')
      .filter((value, index, self) => self.indexOf(value) === index);
    setAvailableQuestionTypes(typeLabels);
    if (lockedQuestionTypeFromUrl) {
      if (
        typeLabels.includes(lockedQuestionTypeFromUrl) &&
        selectedQuestionType !== lockedQuestionTypeFromUrl
      ) {
        setSelectedQuestionType(lockedQuestionTypeFromUrl);
      }
    } else if (typeLabels.length === 1 && !selectedQuestionType) {
      setSelectedQuestionType(typeLabels[0]);
    }
  }, [assessment, lockedQuestionTypeFromUrl, selectedQuestionType]);

  useEffect(() => {
    if (tokenParam) {
      const decoded = decodeShareToken(tokenParam);
      if (decoded) {
        if (decoded.be) {
          localStorage.setItem(EXTERNAL_EMAIL_KEY, decoded.be);
          setLockedParticipantSelection(true);
        }
        if (decoded.typeLabel) {
          setLockedQuestionTypeFromUrl(decoded.typeLabel);
          setSelectedQuestionType((prev) => prev || decoded.typeLabel || null);
        }
        if (decoded.pid) {
          setParticipantIdFromToken(decoded.pid);
        }
      }
    } else {
      const storedEmail = localStorage.getItem(EXTERNAL_EMAIL_KEY);
      if (storedEmail) {
        setEmail(storedEmail);
      } else if (beFromUrl) {
        setEmail(beFromUrl);
        localStorage.setItem(EXTERNAL_EMAIL_KEY, beFromUrl);
        setLockedParticipantSelection(true);
      }
    }
  }, [tokenParam, beFromUrl]);

  // Participant selection logic
  useEffect(() => {
    if (!assessment?.participants || assessment.typeAssessment !== 'Individu') return;

    if (email) {
      const matchByEmail = assessment.participants.find(
        (p: any) => (p.email || '').toLowerCase() === email.toLowerCase()
      );
      if (matchByEmail) {
        const pid = matchByEmail.binusianId || matchByEmail.binusianID;
        if (pid && activeParticipant !== pid) {
          setActiveParticipant(pid);
        }
        return;
      }
    }

    // Match by participantIdFromToken (trAssessmentParticipantID from the shared link)
    if (participantIdFromToken) {
      const matchByToken = assessment.participants.find(
        (p: any) => p.trAssessmentParticipantID === participantIdFromToken
      );
      if (matchByToken) {
        const pid = matchByToken.binusianId || matchByToken.binusianID;
        if (pid && activeParticipant !== pid) {
          setActiveParticipant(pid);
        }
        return;
      }
    }

    // If not locked, allow user to pick; default to first if none selected yet
    if (!lockedParticipantSelection && !activeParticipant) {
      const firstParticipantId = assessment.participants[0].binusianID;
      setActiveParticipant(firstParticipantId);
    }
  }, [assessment, lockedParticipantSelection, email, participantIdFromToken]);

  // Module selection logic - auto-select first module if none selected
  useEffect(() => {
    if (!assessment?.modules || assessment.modules.length === 0) return;

    // If no module is selected, auto-select the first available module
    if (!activeModule) {
      const firstModuleId = assessment.modules[0].moduleID;
      setActiveModule(firstModuleId);
    }
  }, [assessment, activeModule]);

  // Inject lightweight CSS for question highlight pulse if not already present
  useEffect(() => {
    const styleId = 'external-assessment-highlight-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      // Use CSS text content instead of innerHTML to avoid remote property injection
      const cssText = `
        .highlight-pulse { outline: 3px solid #ffa500; animation: hpulse 1.2s ease-in-out 0s 2; }
        @keyframes hpulse { 0% { outline-color:#ffa500; } 50% { outline-color:#ffcd75; } 100% { outline-color:#ffa500; } }
        .unanswered-question-list { list-style:none; margin:0; padding:0; }
        .unanswered-question-item { position:relative; padding:6px 10px 6px 8px; border:1px solid #ffd18a; background:#fff9ed; border-radius:6px; cursor:pointer; display:flex; align-items:flex-start; gap:8px; transition:background .2s ease, box-shadow .2s ease, border-color .2s ease; }
        .unanswered-question-item:not(:last-child){ margin-bottom:6px; }
        .unanswered-question-item:hover { background:#ffe8c2; border-color:#ffbf66; box-shadow:0 1px 3px rgba(0,0,0,0.08); }
        .unanswered-question-item:active { background:#ffd18a; }
        .unanswered-question-item .uq-index { font-size:11px; font-weight:600; color:#b45309; background:#ffedd5; border:1px solid #fbbf24; width:22px; height:22px; display:flex; align-items:center; justify-content:center; border-radius:4px; flex-shrink:0; }
        .unanswered-question-item .uq-text { flex:1; font-size:13px; line-height:1.25; color:#92400e; font-weight:500; }
        .unanswered-question-item .uq-icon { opacity:.65; color:#b45309; transition:transform .2s ease, opacity .2s ease; display:flex; align-items:center; }
        .unanswered-question-item:hover .uq-icon { opacity:1; transform:translateX(3px); }
      `.trim();
      style.appendChild(document.createTextNode(cssText));
      document.head.appendChild(style);
    }
  }, []);

  const handleEmailSubmit = async (submittedEmail: string) => {
    setEmailSubmitting(true);
    try {
      localStorage.setItem(EXTERNAL_EMAIL_KEY, submittedEmail);
      setEmail(submittedEmail);
    } catch {
      showErrorNotification({
        message: 'There was an error accessing the assessment. Please try again.'
      });
    } finally {
      setEmailSubmitting(false);
    }
  };

  useEffect(() => {
    if (assessment?.modules?.length > 0) {
      const initialResponses: Record<string, Record<string, Record<string, string>>> = {};

      assessment.modules.forEach((module: Module) => {
        initialResponses[module.moduleID] = {};

        // For individu type, initialize responses for each participant
        if (assessment.typeAssessment === 'Individu' && assessment.participants) {
          assessment.participants.forEach((participant: Participant) => {
            const participantId =
              (participant as any).binusianId ||
              (participant as any).binusianID ||
              (participant as any).BinusianId ||
              (participant as any).BinusianID;
            if (participantId) {
              initialResponses[module.moduleID][participantId] = {};
            }
          });
        } else {
          // For team type, use the existing structure
          initialResponses[module.moduleID] = { team: {} };
        }
      });

      const draftKey = `${DRAFT_STORAGE_KEY}${encryptedId}_${email}`;
      const savedDraft = localStorage.getItem(draftKey);

      if (savedDraft) {
        try {
          const parsedDraft = JSON.parse(savedDraft);
          // Merge parsed draft with initial responses
          const mergedResponses = { ...initialResponses };

          // Deep merge the draft responses
          Object.keys(parsedDraft).forEach((moduleId) => {
            if (!mergedResponses[moduleId]) {
              mergedResponses[moduleId] = {};
            }
            Object.keys(parsedDraft[moduleId]).forEach((participantKey) => {
              if (!mergedResponses[moduleId][participantKey]) {
                mergedResponses[moduleId][participantKey] = {};
              }
              mergedResponses[moduleId][participantKey] = {
                ...mergedResponses[moduleId][participantKey],
                ...parsedDraft[moduleId][participantKey]
              };
            });
          });

          setResponses(mergedResponses);

          // Check module completion for saved drafts
          if (assessment.typeAssessment === 'Individu' && assessment.participants) {
            assessment.modules.forEach((module: Module) => {
              assessment.participants.forEach((participant: Participant) => {
                const participantId =
                  (participant as any).binusianId ||
                  (participant as any).binusianID ||
                  (participant as any).BinusianId ||
                  (participant as any).BinusianID;
                if (participantId) {
                  const participantResponses = parsedDraft[module.moduleID]?.[participantId] || {};
                  checkModuleCompletion(module.moduleID, participantResponses, participantId);
                }
              });
            });
          } else {
            assessment.modules.forEach((module: Module) => {
              const teamResponses = parsedDraft[module.moduleID]?.team || {};
              checkModuleCompletion(module.moduleID, teamResponses);
            });
          }
        } catch {
          setResponses(initialResponses);
        }
      } else {
        setResponses(initialResponses);
      }
    }
  }, [assessment, encryptedId, email]);

  const checkModuleCompletion = (
    moduleId: string,
    moduleResponses: Record<string, string>,
    participantId?: string
  ) => {
    if (!assessment) return false;

    // Find the module and its questions
    const currentModule = assessment.modules?.find((m: Module) => m.moduleID === moduleId);
    if (!currentModule || !currentModule.questions || currentModule.questions.length === 0) {
      return false;
    }

    // Filter only external questions
    const externalQuestions = currentModule.questions.filter(
      (q: Question) =>
        q.respondentType?.toLowerCase() === 'external' || q.title.toLowerCase().includes('external')
    );

    // If no external questions, consider it complete
    if (externalQuestions.length === 0) return true;

    // Check if all external questions are answered
    const answeredQuestions = externalQuestions.filter(
      (q: Question) =>
        moduleResponses[q.trAssessmentQuestionID] &&
        moduleResponses[q.trAssessmentQuestionID].trim() !== ''
    );

    const isComplete = answeredQuestions.length === externalQuestions.length;

    const completionKey = participantId ? `${moduleId}_${participantId}` : moduleId;

    setCompletedModules((prev) => {
      if (isComplete && !prev.includes(completionKey)) {
        return [...prev, completionKey];
      } else if (!isComplete && prev.includes(completionKey)) {
        return prev.filter((id) => id !== completionKey);
      }
      return prev;
    });

    return isComplete;
  };

  useEffect(() => {
    if (assessment?.modules) {
      if (assessment.typeAssessment === 'Individu' && assessment.participants) {
        assessment.modules.forEach((module: Module) => {
          assessment.participants.forEach((participant: Participant) => {
            const participantId =
              (participant as any).binusianId ||
              (participant as any).binusianID ||
              (participant as any).BinusianId ||
              (participant as any).BinusianID;
            if (participantId) {
              const participantResponses = responses[module.moduleID]?.[participantId] || {};
              checkModuleCompletion(module.moduleID, participantResponses, participantId);
            }
          });
        });
      } else {
        assessment.modules.forEach((module: Module) => {
          const teamResponses = responses[module.moduleID]?.team || {};
          checkModuleCompletion(module.moduleID, teamResponses);
        });
      }
    }
  }, [responses, assessment]);

  const getQuestionStatus = useCallback(
    (moduleId: string, questionId: string, participantId?: string): boolean => {
      if (assessment?.typeAssessment === 'Individu' && participantId) {
        const answer = responses[moduleId]?.[participantId]?.[questionId];
        return !!(answer && answer.trim() !== '');
      } else {
        const answer = responses[moduleId]?.team?.[questionId];
        return !!(answer && answer.trim() !== '');
      }
    },
    [responses, assessment]
  );

  const getCurrentParticipant = () => {
    if (assessment?.typeAssessment === 'Individu') {
      if (activeParticipant) {
        return activeParticipant;
      }
      if (assessment.participants?.length > 0) {
        const firstParticipantId = assessment.participants[0].binusianID;
        if (!activeParticipant) {
          setActiveParticipant(firstParticipantId);
        }
        return firstParticipantId;
      }
      return null;
    }
    return undefined;
  };

  const handleResponseChange = (
    moduleId: string,
    questionId: string,
    value: string,
    participantId?: string
  ) => {
    if (assessment?.typeAssessment === 'Individu' && !participantId) {
      return;
    }

    setResponses((prev: any) => {
      const updatedResponses = { ...prev };

      if (assessment?.typeAssessment === 'Individu' && participantId) {
        if (!updatedResponses[moduleId]) {
          updatedResponses[moduleId] = {};
        }

        if (!updatedResponses[moduleId][participantId]) {
          updatedResponses[moduleId][participantId] = {};
        }

        updatedResponses[moduleId][participantId][questionId] = value;
      } else {
        if (!updatedResponses[moduleId]) {
          updatedResponses[moduleId] = {};
        }

        if (!updatedResponses[moduleId].team) {
          updatedResponses[moduleId].team = {};
        }

        updatedResponses[moduleId].team[questionId] = value;
      }

      setDraftSaved(false);
      return updatedResponses;
    });

    setQuestionCompletionStatus((prev) => {
      const newStatus = { ...prev };

      if (!newStatus[moduleId]) {
        newStatus[moduleId] = {};
      }

      if (assessment?.typeAssessment === 'Individu' && participantId) {
        if (!newStatus[moduleId][participantId]) {
          newStatus[moduleId][participantId] = {};
        }
        newStatus[moduleId][participantId][questionId] = !!(value && value.trim() !== '');
      } else {
        if (!newStatus[moduleId]['team']) {
          newStatus[moduleId]['team'] = {};
        }
        newStatus[moduleId]['team'][questionId] = !!(value && value.trim() !== '');
      }

      return newStatus;
    });
  };

  const cleanupResponses = () => {
    setResponses((prev: any) => {
      const cleaned = { ...prev };

      Object.keys(cleaned).forEach((moduleId) => {
        if (cleaned[moduleId].undefined) {
          delete cleaned[moduleId].undefined;
        }
      });

      return cleaned;
    });
  };

  useEffect(() => {
    if (responses) {
      cleanupResponses();
    }
  }, []);

  useEffect(() => {
    if (!assessment?.modules) return;

    setQuestionCompletionStatus((prevStatus) => {
      const newStatus = { ...prevStatus };

      assessment.modules.forEach((module: Module) => {
        if (!newStatus[module.moduleID]) {
          newStatus[module.moduleID] = {};
        }

        const externalQuestions = (module.questions || []).filter(
          (q: Question) =>
            q.respondentType?.toLowerCase() === 'external' ||
            q.title.toLowerCase().includes('external')
        );

        const relevantQuestions = selectedQuestionType
          ? externalQuestions.filter(
              (q: Question) => (q.typeLabel || 'General') === selectedQuestionType
            )
          : externalQuestions;

        if (assessment.typeAssessment === 'Individu' && assessment.participants) {
          assessment.participants.forEach((participant: Participant) => {
            const participantId =
              (participant as any).binusianId ||
              (participant as any).binusianID ||
              (participant as any).BinusianId ||
              (participant as any).BinusianID;
            if (participantId) {
              if (!newStatus[module.moduleID][participantId]) {
                newStatus[module.moduleID][participantId] = {};
              }

              relevantQuestions.forEach((question: Question) => {
                const currentAnswer = getQuestionStatus(
                  module.moduleID,
                  question.trAssessmentQuestionID,
                  participantId
                );
                if (
                  newStatus[module.moduleID][participantId][question.trAssessmentQuestionID] !==
                  currentAnswer
                ) {
                  newStatus[module.moduleID][participantId][question.trAssessmentQuestionID] =
                    currentAnswer;
                }
              });
            }
          });
        } else {
          if (!newStatus[module.moduleID]['team']) {
            newStatus[module.moduleID]['team'] = {};
          }

          relevantQuestions.forEach((question: Question) => {
            const currentAnswer = getQuestionStatus(
              module.moduleID,
              question.trAssessmentQuestionID
            );
            if (
              newStatus[module.moduleID]['team'][question.trAssessmentQuestionID] !== currentAnswer
            ) {
              newStatus[module.moduleID]['team'][question.trAssessmentQuestionID] = currentAnswer;
            }
          });
        }
      });

      return newStatus;
    });
  }, [responses, assessment, getQuestionStatus, selectedQuestionType]);

  const saveDraft = () => {
    showSuccessNotification({
      message: 'Draft saved successfully'
    });

    const draftKey = `${DRAFT_STORAGE_KEY}${encryptedId}_${email}`;
    localStorage.setItem(draftKey, JSON.stringify(responses));
    setDraftSaved(true);

    setTimeout(() => {
      setDraftSaved(false);
    }, 3000);
  };

  const getUnassessedParticipants = (): Participant[] => {
    if (!assessment || assessment.typeAssessment !== 'Individu' || !assessment.participants) {
      return [];
    }

    const unassessed: Participant[] = [];

    for (const participant of assessment.participants) {
      const participantId = participant.binusianId || participant.binusianID;
      if (!participantId) continue;

      let hasAnyAnswer = false;

      // Check if participant has any answers across all modules
      for (const module of assessment.modules || []) {
        const participantResponses = responses[module.moduleID]?.[participantId] || {};
        const hasAnswerInModule = Object.values(participantResponses).some(
          (answer) => answer && answer.trim() !== ''
        );

        if (hasAnswerInModule) {
          hasAnyAnswer = true;
          break;
        }
      }

      if (!hasAnyAnswer) {
        unassessed.push(participant);
      }
    }

    return unassessed;
  };

  const submitMutation = useMutation({
    mutationFn: (responseData: any) =>
      assessmentScheduleService.submitExternalAssessmentResponse(
        encryptedId,
        email || '',
        responseData
      ),
    onSuccess: () => {
      setFormSubmitted(true);
      setFormError('');
      const draftKey = `${DRAFT_STORAGE_KEY}${encryptedId}_${email}`;
      localStorage.removeItem(draftKey);
      showSuccessNotification({
        message: 'Assessment submitted successfully'
      });
    },
    onError: (error: any) => {
      setFormError(error.message || 'Failed to submit your assessment. Please try again.');
      showErrorNotification({
        message: 'Error submitting assessment'
      });
    }
  });

  const handleSubmit = (skipWarning: boolean = false) => {
    if (!assessment) return;

    if (!skipWarning && assessment.typeAssessment === 'Individu' && !lockedParticipantSelection) {
      const unassessed = getUnassessedParticipants();
      if (unassessed.length > 0) {
        setUnassessedParticipants(unassessed);
        setShowWarningModal(true);
        return;
      }
    }

    // Pre-submit validation: ensure minimum completion
    const hasAnswerForAllExternalQuestions = () => {
      if (!assessment?.modules || assessment.modules.length === 0) return false;

      const isNonEmpty = (v?: string) => !!(v && v.trim() !== '');

      // Helper to get external questions for a module
      const getExternalQs = (module: Module) =>
        (module.questions || []).filter(
          (q: Question) =>
            q.respondentType?.toLowerCase() === 'external' ||
            q.title.toLowerCase().includes('external')
        );

      if (assessment.typeAssessment === 'Individu' && assessment.participants?.length) {
        for (const module of assessment.modules) {
          const externalQs = getExternalQs(module);
          if (externalQs.length === 0) continue; // skip modules without external questions

          // group questions by role/typeLabel (fallback to 'General')
          const groups = externalQs.reduce(
            (acc: Record<string, Question[]>, q: Question) => {
              const key = q.typeLabel || 'General';
              (acc[key] ||= []).push(q);
              return acc;
            },
            {} as Record<string, Question[]>
          );

          for (const participant of assessment.participants) {
            const participantId =
              (participant as any).binusianId ||
              (participant as any).binusianID ||
              (participant as any).BinusianId ||
              (participant as any).BinusianID;
            if (!participantId) continue;

            const participantResponses = responses[module.moduleID]?.[participantId] || {};

            // if any group within this module is fully answered for this participant, it's enough
            const anyGroupComplete = Object.values(groups).some((qs) =>
              qs.every((q) => isNonEmpty(participantResponses[q.trAssessmentQuestionID]))
            );
            if (anyGroupComplete) return true;
          }
        }
        return false;
      } else {
        // Team: Need at least one module where, for ANY one role group (typeLabel), all questions are answered
        for (const module of assessment.modules) {
          const externalQs = getExternalQs(module);
          if (externalQs.length === 0) continue; // skip modules without external questions

          // group questions by role/typeLabel (fallback to 'General')
          const groups = externalQs.reduce(
            (acc: Record<string, Question[]>, q: Question) => {
              const key = q.typeLabel || 'General';
              (acc[key] ||= []).push(q);
              return acc;
            },
            {} as Record<string, Question[]>
          );

          const teamResponses = responses[module.moduleID]?.team || {};
          const anyGroupComplete = Object.values(groups).some((qs) =>
            qs.every((q) => isNonEmpty(teamResponses[q.trAssessmentQuestionID]))
          );
          if (anyGroupComplete) return true;
        }
        return false;
      }
    };

    const meetsMinimum = hasAnswerForAllExternalQuestions();
    if (!meetsMinimum) {
      const msg =
        assessment.typeAssessment === 'Individu'
          ? 'Please complete all external questions for at least one participant (any one role group) in at least one module before submitting.'
          : 'Please complete all external questions for any one role group in at least one module before submitting.';
      setFormError(msg);
      showErrorNotification({ message: msg });
      return;
    }

    let responseData: any = {
      msAssessmentScheduleID: assessment.msAssessmentScheduleID,
      externalEmail: email,
      hash: encryptedId,
      typeAssessment: assessment.typeAssessment || 'Team'
    };

    if (assessment.typeAssessment === 'Individu' && assessment.participants) {
      const participantResponses = [];

      // Loop through all participants regardless of whether they have responses
      for (const participant of assessment.participants) {
        const participantModules = [];
        let hasAnyResponses = false;

        for (const module of assessment.modules) {
          // Convert binusianId/binusianID to consistent case
          const participantId = participant.binusianId || participant.binusianID;

          // Get participant responses, log them for debugging
          const participantModuleResponses = responses[module.moduleID]?.[participantId] || {};

          if (Object.keys(participantModuleResponses).length === 0) continue;

          const moduleQs = module.questions || [];

          // Filter only external questions
          const externalQuestions = moduleQs.filter(
            (q: Question) =>
              q.respondentType?.toLowerCase() === 'external' ||
              q.title.toLowerCase().includes('external')
          );

          // Build questions array, including only external questions
          const questions = externalQuestions
            .map((question: Question) => {
              const answer = participantModuleResponses[question.trAssessmentQuestionID] || '';
              return {
                trAssessmentQuestionID: question.trAssessmentQuestionID,
                answer: answer,
                type: question.type
              };
            })
            .filter((q: any) => q.answer !== ''); // Only keep non-empty answers

          if (questions.length === 0) continue;

          hasAnyResponses = true;

          // For external users, we don't include evidences
          participantModules.push({
            moduleID: module.moduleID,
            trAssessmentModuleID: module.trAssessmentModuleID,
            questions
          });
        }

        if (hasAnyResponses) {
          participantResponses.push({
            trAssessmentParticipantID: participant.trAssessmentParticipantID,
            modules: participantModules
          });
        }
      }

      responseData.participantResponses = participantResponses;
    } else {
      const modules = [];

      for (const module of assessment.modules) {
        const moduleQs = module.questions || [];

        // Filter only external questions
        const externalQuestions = moduleQs.filter(
          (q: Question) =>
            q.respondentType?.toLowerCase() === 'external' ||
            q.title.toLowerCase().includes('external')
        );

        const teamResponses = responses[module.moduleID]?.team || {};

        if (Object.keys(teamResponses).length === 0) continue;

        const questions = externalQuestions
          .map((question: any) => ({
            trAssessmentQuestionID: question.trAssessmentQuestionID,
            answer: teamResponses[question.trAssessmentQuestionID] || '',
            type: question.type
          }))
          .filter((q: any) => q.answer !== '');

        if (questions.length === 0) continue;

        // For external users, we don't include evidences
        modules.push({
          moduleID: module.moduleID,
          trAssessmentModuleID: module.trAssessmentModuleID,
          questions
        });
      }

      responseData.modules = modules;
    }

    submitMutation.mutate(responseData);
  };

  if (!encryptedId) {
    return (
      <Container size='lg' py='xl'>
        <Title order={2} mb='md'>
          Assessment Detail
        </Title>
        <Paper p='md' withBorder>
          <Text color='red'>
            Missing required parameters. Please use the link provided in your email.
          </Text>
        </Paper>
      </Container>
    );
  }

  // Show email form if email is not set
  if (!email) {
    return (
      <Container size='sm' py='xl'>
        <Title order={2} mb='xl'>
          Dacum 2.0 | LIA
        </Title>
        <EmailForm onSubmit={handleEmailSubmit} loading={emailSubmitting} />
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container fluid>
        <LoadingOverlay visible={true} />
      </Container>
    );
  }

  if (error || !assessment) {
    return <NotFoundPage />;
  }

  const status = getAssessmentAvailabilityStatus(
    assessment.startTime,
    assessment.endTime,
    assessment.isDeleted
  );

  const handleSubmitOrNavigate = () => {
    setFormError('');

    if (submitMutation.isPending) return;

    handleSubmit();
  };

  const handleChangeEmail = () => {
    localStorage.removeItem(EXTERNAL_EMAIL_KEY);
    setEmail('');
    setLockedParticipantSelection(false);
  };

  const handleConfirmSubmit = () => {
    setShowWarningModal(false);
    handleSubmit(true);
  };

  if (formSubmitted) {
    return (
      <Center maw={'full'} h={'100vh'}>
        <Container size='md' py='xl'>
          <Card withBorder radius='lg' padding='xl'>
            <Stack align='center' gap='sm'>
              <IconCircleCheck size={64} stroke={1.5} color='#4CAF50' />
              <Title order={2} ta='center' fw={700}>
                Assessment Submitted Successfully
              </Title>
              <Text ta='center' size='lg'>
                Thank you for completing the assessment. Your responses have been recorded.
              </Text>
              <Text ta='center' c='dimmed'>
                Your assessment will be reviewed by an administrator.
              </Text>
              <Button variant='light' onClick={handleChangeEmail} mt='md'>
                Change Email
              </Button>
            </Stack>
          </Card>
        </Container>
      </Center>
    );
  }

  return (
    <>
      <Modal
        opened={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        title={
          <Text size='xl' fw={700}>
            Unassessed Participants Warning
          </Text>
        }
        centered
        size='lg'
      >
        <Stack gap='md'>
          <Alert icon={<IconAlertCircle size={16} />} color='orange' title='Warning'>
            You are about to submit this assessment, but some participants have not been assessed
            yet. Are you sure you want to continue?
          </Alert>

          <Box>
            <Text fw={600} mb='sm'>
              Participants not yet assessed ({unassessedParticipants.length}):
            </Text>
            <Paper withBorder p='md' style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <List spacing='sm' withPadding>
                {unassessedParticipants.map((participant, index) => (
                  <List.Item key={participant.trAssessmentParticipantID || index}>
                    <Group gap='xs'>
                      <Avatar color='orange' radius='xl' size='sm'>
                        {(participant.name || participant.binusianName || '?')
                          .charAt(0)
                          .toUpperCase()}
                      </Avatar>
                      <Stack gap={0}>
                        <Text size='sm' fw={500}>
                          {participant.name || participant.binusianName}
                        </Text>
                        <Text size='xs' c='dimmed'>
                          {participant.binusianID || participant.binusianId} |{' '}
                          {participant.division || 'N/A'}
                        </Text>
                      </Stack>
                    </Group>
                  </List.Item>
                ))}
              </List>
            </Paper>
          </Box>

          <Text size='sm' c='dimmed'>
            If you continue, the assessment will be submitted with responses only for the
            participants you have assessed.
          </Text>

          <Group justify='flex-end' mt='md'>
            <Button variant='outline' onClick={() => setShowWarningModal(false)}>
              Cancel
            </Button>
            <Button
              color='orange'
              onClick={handleConfirmSubmit}
              leftSection={<IconSend size={16} />}
            >
              Continue and Submit
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Container size='lg' py={{ base: 'md', sm: 'xl' }}>
        <Grid gutter='lg'>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap='md'>
              <Card
                withBorder
                radius='lg'
                padding='lg'
                style={{ backgroundColor: '#0F172A', color: '#fff' }}
              >
                <Group justify='space-between' align='flex-start'>
                  <Stack gap={14}>
                    <Group gap='xs'>
                      {status === 'completed' ? (
                        <Badge color='teal' variant='light' size='lg' radius='sm'>
                          Completed on {dayjs.utc(assessment.endTime).local().format('DD/MM/YYYY')}
                        </Badge>
                      ) : status === 'in progress' ? (
                        <Badge color='yellow' variant='light' size='lg' radius='sm'>
                          In Progress
                        </Badge>
                      ) : status === 'cancelled' ? (
                        <Badge color='red' variant='light' size='lg' radius='sm'>
                          Cancelled
                        </Badge>
                      ) : (
                        <Badge color='blue' variant='light' size='lg' radius='sm'>
                          Pending
                        </Badge>
                      )}
                    </Group>

                    <Title order={2} c='#fff'>
                      {assessment.name}
                    </Title>

                    <Group gap='xs'>
                      <Badge color='gray' bg='gray' variant='solid' size='sm'>
                        {assessment.type}
                      </Badge>
                      <Badge color='gray' bg='gray' variant='solid' size='sm'>
                        {assessment.typeAssessment || 'Team'}
                      </Badge>
                      {assessment.source && (
                        <Badge color='gray' bg='gray' variant='solid' size='sm'>
                          Source: {assessment.source}
                        </Badge>
                      )}
                    </Group>
                  </Stack>
                </Group>
              </Card>

              <Card withBorder radius='lg' padding='lg'>
                <Title order={4} mb='md'>
                  About this assessment
                </Title>
                <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {assessment.description}
                </Text>
                <Title order={4} mt='xl' mb='md'>
                  Notes
                </Title>
                <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {assessment.notes === '' ? '-' : assessment.notes}
                </Text>
              </Card>

              <Card withBorder radius='lg' padding='lg'>
                <Title order={4} mb='md'>
                  Assessment Questions
                </Title>

                {status !== 'in progress' ? (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    title='Assessment Not Active'
                    color='blue'
                  >
                    {status === 'pending' ? (
                      <>
                        <Text>This assessment has not started yet.</Text>
                        <Text size='sm' mt='xs' fw={500}>
                          Start Date:{' '}
                          {dayjs.utc(assessment.startTime).local().format('DD/MM/YYYY HH:mm')}
                        </Text>
                      </>
                    ) : status === 'completed' ? (
                      <>
                        <Text>This assessment has already ended.</Text>
                        <Text size='sm' mt='xs' fw={500}>
                          End Date:{' '}
                          {dayjs.utc(assessment.endTime).local().format('DD/MM/YYYY HH:mm')}
                        </Text>
                      </>
                    ) : (
                      <Text>This assessment is not currently available.</Text>
                    )}
                  </Alert>
                ) : (
                  <>
                    <Paper p='md' withBorder mb='xl'>
                      <Group gap='md'>
                        <Avatar color='#7B2183' radius='xl' size='md'>
                          {email?.charAt(0).toUpperCase()}
                        </Avatar>
                        <div>
                          <Text size='sm' fw={500}>
                            You are participating as:
                          </Text>
                          <Text fw={700}>{email}</Text>
                          <Text size='xs' color='dimmed'>
                            External Respondent
                          </Text>
                        </div>
                      </Group>
                    </Paper>

                    <Alert
                      icon={
                        assessment.typeAssessment === 'Individu' ? (
                          <IconUser size={16} />
                        ) : (
                          <IconUsers size={16} />
                        )
                      }
                      color='blue'
                      mb='lg'
                    >
                      This is a <b>{assessment.typeAssessment || 'Team'}</b> assessment.
                      {assessment.typeAssessment === 'Individu'
                        ? ' You need to evaluate each participant individually.'
                        : ' You need to evaluate the team as a whole.'}
                      {assessment.typeAssessment !== 'Individu' &&
                        assessment.participants &&
                        Array.isArray(assessment.participants) &&
                        assessment.participants.length > 0 && (
                          <>
                            <Text size='sm' mt='sm' fw={600}>
                              Participants:
                            </Text>
                            <List size='sm' spacing='xs' withPadding>
                              {assessment.participants.map((p: any) => (
                                <List.Item
                                  key={p.trAssessmentParticipantID || p.binusianID}
                                  style={{ display: 'flex', flexDirection: 'column' }}
                                >
                                  <Text size='sm'>
                                    {p.name || p.binusianName} ({p.binusianID}), {p.division || '-'}
                                  </Text>
                                </List.Item>
                              ))}
                            </List>
                          </>
                        )}
                    </Alert>

                    {assessment.modules &&
                      assessment.modules.length > 0 &&
                      (() => {
                        const filteredModules = assessment.modules.filter((module: Module) => {
                          const externalQs = (module.questions || []).filter(
                            (q: Question) =>
                              q.respondentType?.toLowerCase() === 'external' ||
                              q.title.toLowerCase().includes('external')
                          );
                          if (externalQs.length === 0) return false; // hide modules with zero external questions entirely
                          if (!selectedQuestionType) return true; // no role chosen yet -> show modules that have external questions
                          return externalQs.some(
                            (q) => (q.typeLabel || 'General') === selectedQuestionType
                          );
                        });

                        // If activeModule is no longer in filtered list, switch to first available
                        if (
                          activeModule &&
                          !filteredModules.some((m: Module) => m.moduleID === activeModule)
                        ) {
                          if (filteredModules.length > 0) {
                            setActiveModule(filteredModules[0].moduleID);
                          } else {
                            setActiveModule(null);
                          }
                        }

                        if (filteredModules.length === 0) {
                          return (
                            <Paper p='md' withBorder mb='lg'>
                              <Stack gap='xs'>
                                <Alert icon={<IconAlertCircle size={16} />} color='yellow'>
                                  {selectedQuestionType ? (
                                    <Text>
                                      No modules contain questions for role: {selectedQuestionType}.
                                    </Text>
                                  ) : (
                                    <Text>No external-question modules are available.</Text>
                                  )}
                                </Alert>
                                {!selectedQuestionType && availableQuestionTypes.length > 0 && (
                                  <Text size='xs' c='dimmed'>
                                    Choose a role above to see if modules become available.
                                  </Text>
                                )}
                              </Stack>
                            </Paper>
                          );
                        }

                        return (
                          <ScrollArea>
                            <Tabs
                              value={activeModule}
                              onChange={(value) => {
                                setActiveModule(value);
                              }}
                              mb='xl'
                            >
                              <Tabs.List>
                                {filteredModules.map((module: Module) => (
                                  <Tabs.Tab value={module.moduleID} key={module.moduleID}>
                                    <Group gap={6} align='center'>
                                      <Text fw={500} size='sm' c='dimmed'>
                                        {module.moduleName || `${module.moduleID}`}
                                      </Text>
                                      {activeModule === module.moduleID && (
                                        <Badge size='xs' color='blue' variant='filled'>
                                          Selected
                                        </Badge>
                                      )}
                                    </Group>
                                  </Tabs.Tab>
                                ))}
                              </Tabs.List>
                            </Tabs>
                          </ScrollArea>
                        );
                      })()}

                    {/* Type selector dropdown */}
                    {activeModule && availableQuestionTypes.length > 0 && (
                      <Box mb='xl'>
                        <Paper p='md' withBorder>
                          <Group justify='space-between' align='center'>
                            <Group>
                              <IconFilter size={18} />
                              <Text fw={600}>Select Role</Text>
                              {/* Progress for selected type */}
                              {selectedQuestionType &&
                                (() => {
                                  const currentModule = assessment.modules.find(
                                    (m: Module) => m.moduleID === activeModule
                                  );
                                  if (!currentModule) return null;

                                  const externalQuestions = (currentModule.questions || []).filter(
                                    (q: Question) =>
                                      q.respondentType?.toLowerCase() === 'external' ||
                                      q.title.toLowerCase().includes('external')
                                  );

                                  const typeQuestions = externalQuestions.filter(
                                    (q: Question) =>
                                      (q.typeLabel || 'General') === selectedQuestionType
                                  );

                                  if (typeQuestions.length === 0) return null;

                                  const completedQuestions = typeQuestions.filter((q: Question) => {
                                    if (
                                      assessment?.typeAssessment === 'Individu' &&
                                      activeParticipant
                                    ) {
                                      return questionCompletionStatus[activeModule]?.[
                                        activeParticipant
                                      ]?.[q.trAssessmentQuestionID];
                                    } else {
                                      return questionCompletionStatus[activeModule]?.['team']?.[
                                        q.trAssessmentQuestionID
                                      ];
                                    }
                                  }).length;

                                  const percentage = Math.round(
                                    (completedQuestions / typeQuestions.length) * 100
                                  );

                                  return (
                                    <Group gap='xs'>
                                      <Badge
                                        color={
                                          percentage === 100
                                            ? 'green'
                                            : percentage > 0
                                              ? 'yellow'
                                              : 'red'
                                        }
                                        size='sm'
                                        variant='outline'
                                      >
                                        {completedQuestions}/{typeQuestions.length}
                                      </Badge>
                                      <Box style={{ width: '80px' }}>
                                        <div
                                          style={{
                                            width: '100%',
                                            height: '6px',
                                            backgroundColor: '#f1f1f1',
                                            borderRadius: '3px',
                                            overflow: 'hidden'
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: `${percentage}%`,
                                              height: '100%',
                                              backgroundColor:
                                                percentage === 100
                                                  ? '#28a745'
                                                  : percentage > 0
                                                    ? '#ffc107'
                                                    : '#dc3545',
                                              transition: 'width 0.3s ease'
                                            }}
                                          />
                                        </div>
                                      </Box>
                                    </Group>
                                  );
                                })()}
                            </Group>
                            <Stack gap={4} style={{ minWidth: 220 }}>
                              <Select
                                data={availableQuestionTypes.map((type) => ({
                                  value: type,
                                  label: type
                                }))}
                                value={selectedQuestionType}
                                onChange={(val) => {
                                  // Allow changes unless locked from URL token
                                  if (lockedQuestionTypeFromUrl) return; // fully locked
                                  setSelectedQuestionType(val);
                                }}
                                placeholder='Select role'
                                clearable={false}
                                disabled={!!lockedQuestionTypeFromUrl}
                              />
                              {lockedQuestionTypeFromUrl && selectedQuestionType && (
                                <Text size='xs' c='dimmed'>
                                  Role locked from link as{' '}
                                  <Text span fw={600}>
                                    {lockedQuestionTypeFromUrl}
                                  </Text>
                                  .
                                </Text>
                              )}
                              {/* No lock message for manual selection so user can change freely */}
                            </Stack>
                          </Group>
                        </Paper>
                      </Box>
                    )}

                    {assessment?.typeAssessment === 'Individu' &&
                      assessment?.participants &&
                      Array.isArray(assessment.participants) &&
                      assessment.participants.length > 0 && (
                        <Box mb='xl'>
                          <Title order={5} mb='sm'>
                            Participant
                          </Title>
                          {lockedParticipantSelection ? (
                            <Text size='sm' c='dimmed'>
                              Participant locked as{' '}
                              <Text span fw={600}>
                                {assessment.participants.find(
                                  (p: any) =>
                                    p.binusianID === activeParticipant ||
                                    p.binusianId === activeParticipant ||
                                    p.trAssessmentParticipantID === participantIdFromToken
                                )?.name ||
                                  activeParticipant ||
                                  participantIdFromToken ||
                                  '-'}
                              </Text>{' '}
                              (derived from shared link email)
                            </Text>
                          ) : (
                            <Select
                              value={activeParticipant || ''}
                              onChange={(value) => {
                                setActiveParticipant(value);
                              }}
                              data={assessment.participants.map((participant: any) => ({
                                value: participant.binusianID,
                                label: participant.name || participant.binusianID
                              }))}
                              placeholder='Select a participant'
                              clearable={false}
                            />
                          )}
                        </Box>
                      )}

                    {formError && (
                      <Alert icon={<IconAlertCircle size={16} />} title='Error' color='red' mb='md'>
                        {formError}
                      </Alert>
                    )}

                    {/* Save draft notification */}
                    {draftSaved && (
                      <Alert
                        icon={<IconCheck size={16} />}
                        title='Draft Saved'
                        color='green'
                        mb='md'
                      >
                        Your progress has been saved locally. You can continue later.
                      </Alert>
                    )}

                    {activeModule && !selectedQuestionType && (
                      <Paper p='lg' withBorder mb='lg'>
                        <Stack gap='sm'>
                          <Alert
                            icon={<IconAlertCircle size={16} />}
                            color='blue'
                            title='Select a Role'
                          >
                            Please choose a role from the "Select Role" dropdown above so the
                            questions for this module can be displayed.
                          </Alert>
                          <Text size='sm' c='dimmed'>
                            Roles group external questions by context (e.g. different stakeholder
                            perspectives). Once you pick one, the related questions will appear
                            here.
                          </Text>
                        </Stack>
                      </Paper>
                    )}

                    {activeModule && selectedQuestionType && (
                      <div ref={questionSectionRef}>
                        <Paper p='lg' withBorder mb='lg'>
                          <Stack gap='md'>
                            <Group justify='space-between'>
                              <Title order={4}>{selectedQuestionType} Questions</Title>
                              <Group gap='sm'>
                                <Badge color='indigo' size='lg'>
                                  Type: {selectedQuestionType}
                                </Badge>
                                {/* Overall progress for this question type */}
                                {(() => {
                                  const currentModule = assessment.modules.find(
                                    (m: Module) => m.moduleID === activeModule
                                  );
                                  if (!currentModule || !currentModule.questions) return null;

                                  const filteredQuestions = currentModule.questions.filter(
                                    (q: Question) => {
                                      const isExternal =
                                        q.respondentType?.toLowerCase() === 'external' ||
                                        q.title.toLowerCase().includes('external');
                                      const matchesType =
                                        (q.typeLabel || 'General') === selectedQuestionType;
                                      return isExternal && matchesType;
                                    }
                                  );

                                  if (filteredQuestions.length === 0) return null;

                                  let completed = 0;
                                  filteredQuestions.forEach((q: Question) => {
                                    const isAnswered =
                                      assessment?.typeAssessment === 'Individu' && activeParticipant
                                        ? questionCompletionStatus[activeModule]?.[
                                            activeParticipant
                                          ]?.[q.trAssessmentQuestionID]
                                        : questionCompletionStatus[activeModule]?.['team']?.[
                                            q.trAssessmentQuestionID
                                          ];

                                    if (isAnswered) completed++;
                                  });

                                  const percentage = Math.round(
                                    (completed / filteredQuestions.length) * 100
                                  );

                                  return (
                                    <Group gap='xs'>
                                      <Badge
                                        color={
                                          percentage === 100
                                            ? 'green'
                                            : percentage > 0
                                              ? 'yellow'
                                              : 'red'
                                        }
                                        size='md'
                                        variant='outline'
                                      >
                                        {completed}/{filteredQuestions.length} Complete
                                      </Badge>
                                      <Box style={{ width: '120px' }}>
                                        <div
                                          style={{
                                            width: '100%',
                                            height: '8px',
                                            backgroundColor: '#f1f1f1',
                                            borderRadius: '4px',
                                            overflow: 'hidden',
                                            marginBottom: '4px'
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: `${percentage}%`,
                                              height: '100%',
                                              backgroundColor:
                                                percentage === 100
                                                  ? '#28a745'
                                                  : percentage > 0
                                                    ? '#ffc107'
                                                    : '#dc3545',
                                              transition: 'width 0.3s ease'
                                            }}
                                          />
                                        </div>
                                        <Text size='xs' ta='center' c='dimmed'>
                                          {percentage}% Complete
                                        </Text>
                                      </Box>
                                    </Group>
                                  );
                                })()}
                              </Group>
                            </Group>

                            {(() => {
                              // Get current module
                              const currentModule = assessment.modules.find(
                                (m: Module) => m.moduleID === activeModule
                              );
                              if (!currentModule || !currentModule.questions) {
                                return (
                                  <Text ta='center' color='dimmed'>
                                    No questions available for this module.
                                  </Text>
                                );
                              }

                              // Filter questions by selected type
                              const filteredQuestions = currentModule.questions.filter(
                                (q: Question) => {
                                  const isExternal =
                                    q.respondentType?.toLowerCase() === 'external' ||
                                    q.title.toLowerCase().includes('external');
                                  const matchesType =
                                    (q.typeLabel || 'General') === selectedQuestionType;
                                  return isExternal && matchesType;
                                }
                              );

                              if (filteredQuestions.length === 0) {
                                return (
                                  <Alert icon={<IconAlertCircle size={16} />} color='yellow'>
                                    <Text ta='center'>
                                      No questions found for type: {selectedQuestionType}
                                    </Text>
                                    <Text size='sm' ta='center' c='dimmed' mt='xs'>
                                      Try selecting a different question type from the dropdown
                                      above.
                                    </Text>
                                  </Alert>
                                );
                              }

                              return (
                                <Stack gap='xl'>
                                  {filteredQuestions.map((question: Question, index: number) => (
                                    <Box
                                      key={question.trAssessmentQuestionID || index}
                                      p='md'
                                      data-question-id={question.trAssessmentQuestionID}
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        border: (() => {
                                          const isAnswered =
                                            assessment?.typeAssessment === 'Individu' &&
                                            activeParticipant
                                              ? questionCompletionStatus[activeModule]?.[
                                                  activeParticipant
                                                ]?.[question.trAssessmentQuestionID]
                                              : questionCompletionStatus[activeModule]?.['team']?.[
                                                  question.trAssessmentQuestionID
                                                ];

                                          return isAnswered
                                            ? '2px solid #28a745'
                                            : '2px solid #ffa500';
                                        })(),
                                        position: 'relative',
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      {/* Question Status Indicator */}
                                      <Group
                                        style={{
                                          position: 'absolute',
                                          top: '12px',
                                          right: '12px',
                                          zIndex: 10
                                        }}
                                        gap={4}
                                      >
                                        <Tooltip
                                          label={(() => {
                                            const isAnswered =
                                              assessment?.typeAssessment === 'Individu' &&
                                              activeParticipant
                                                ? questionCompletionStatus[activeModule]?.[
                                                    activeParticipant
                                                  ]?.[question.trAssessmentQuestionID]
                                                : questionCompletionStatus[activeModule]?.[
                                                    'team'
                                                  ]?.[question.trAssessmentQuestionID];

                                            return isAnswered
                                              ? 'Question answered'
                                              : 'Question required';
                                          })()}
                                        >
                                          <Badge
                                            color={(() => {
                                              const isAnswered =
                                                assessment?.typeAssessment === 'Individu' &&
                                                activeParticipant
                                                  ? questionCompletionStatus[activeModule]?.[
                                                      activeParticipant
                                                    ]?.[question.trAssessmentQuestionID]
                                                  : questionCompletionStatus[activeModule]?.[
                                                      'team'
                                                    ]?.[question.trAssessmentQuestionID];

                                              return isAnswered ? 'green' : 'orange';
                                            })()}
                                            size='sm'
                                            variant='filled'
                                            style={{ cursor: 'help' }}
                                          >
                                            {(() => {
                                              const isAnswered =
                                                assessment?.typeAssessment === 'Individu' &&
                                                activeParticipant
                                                  ? questionCompletionStatus[activeModule]?.[
                                                      activeParticipant
                                                    ]?.[question.trAssessmentQuestionID]
                                                  : questionCompletionStatus[activeModule]?.[
                                                      'team'
                                                    ]?.[question.trAssessmentQuestionID];

                                              return isAnswered ? '✓' : '!';
                                            })()}
                                          </Badge>
                                        </Tooltip>
                                      </Group>

                                      <Group gap={8} align='flex-start'>
                                        <Text fw={500} size='md' color='#333'>
                                          {index + 1}.
                                        </Text>
                                        <Stack gap={4} style={{ flex: 1, paddingRight: '60px' }}>
                                          <Group justify='space-between' align='flex-start'>
                                            <Text fw={600} size='md' color='#333'>
                                              {question.title}
                                            </Text>
                                          </Group>

                                          {question.description && (
                                            <Text size='sm' color='dimmed' mt={6}>
                                              {question.description}
                                            </Text>
                                          )}

                                          {/* Add input elements for each question type */}
                                          {status === 'in progress' && (
                                            <Box mt='md'>
                                              {question.type === 'Range' && question.rangeItems && (
                                                <Radio.Group
                                                  value={
                                                    assessment.typeAssessment === 'Individu' &&
                                                    activeParticipant
                                                      ? responses[activeModule]?.[
                                                          activeParticipant
                                                        ]?.[question.trAssessmentQuestionID] || ''
                                                      : responses[activeModule]?.team?.[
                                                          question.trAssessmentQuestionID
                                                        ] || ''
                                                  }
                                                  onChange={(value) =>
                                                    handleResponseChange(
                                                      activeModule,
                                                      question.trAssessmentQuestionID,
                                                      value,
                                                      getCurrentParticipant()
                                                    )
                                                  }
                                                >
                                                  <Stack gap='xs'>
                                                    {question.rangeItems
                                                      .slice()
                                                      .sort((a, b) => a.sequence - b.sequence)
                                                      .map((item) => (
                                                        <Radio
                                                          key={item.trAssessmentQuestionRangeItemID}
                                                          value={item.value}
                                                          label={`${item.label} (${item.value})`}
                                                        />
                                                      ))}
                                                  </Stack>
                                                </Radio.Group>
                                              )}

                                              {question.type === 'Free-text' && (
                                                <Textarea
                                                  placeholder='Enter your response...'
                                                  minRows={3}
                                                  value={
                                                    assessment.typeAssessment === 'Individu' &&
                                                    activeParticipant
                                                      ? responses[activeModule]?.[
                                                          activeParticipant
                                                        ]?.[question.trAssessmentQuestionID] || ''
                                                      : responses[activeModule]?.team?.[
                                                          question.trAssessmentQuestionID
                                                        ] || ''
                                                  }
                                                  onChange={(e) =>
                                                    handleResponseChange(
                                                      activeModule,
                                                      question.trAssessmentQuestionID,
                                                      e.target.value,
                                                      getCurrentParticipant()
                                                    )
                                                  }
                                                />
                                              )}
                                            </Box>
                                          )}
                                        </Stack>
                                      </Group>
                                    </Box>
                                  ))}
                                </Stack>
                              );
                            })()}
                            {(() => {
                              const currentModule = assessment.modules.find(
                                (m: Module) => m.moduleID === activeModule
                              );
                              if (!currentModule || !currentModule.questions) return null;
                              const filteredQuestions = currentModule.questions.filter(
                                (q: Question) => {
                                  const isExternal =
                                    q.respondentType?.toLowerCase() === 'external' ||
                                    q.title.toLowerCase().includes('external');
                                  const matchesType =
                                    (q.typeLabel || 'General') === selectedQuestionType;
                                  return isExternal && matchesType;
                                }
                              );
                              if (!filteredQuestions.length) return null;
                              const unanswered = filteredQuestions.filter((q: Question) => {
                                const answered =
                                  assessment?.typeAssessment === 'Individu' && activeParticipant
                                    ? questionCompletionStatus[activeModule]?.[activeParticipant]?.[
                                        q.trAssessmentQuestionID
                                      ]
                                    : questionCompletionStatus[activeModule]?.['team']?.[
                                        q.trAssessmentQuestionID
                                      ];
                                return !answered;
                              });
                              if (unanswered.length === 0) return null;
                              return (
                                <Alert
                                  icon={<IconAlertCircle size={16} />}
                                  title='Incomplete Questions'
                                  color='orange'
                                >
                                  <Stack gap={6}>
                                    <Text size='sm'>Unanswered questions in this module:</Text>
                                    <List
                                      size='sm'
                                      spacing={4}
                                      withPadding={false}
                                      style={{ marginLeft: 4 }}
                                    >
                                      {unanswered.map((q: Question) => (
                                        <List.Item
                                          key={q.trAssessmentQuestionID}
                                          style={{
                                            cursor: 'pointer',
                                            color: '#1976d2',
                                            textDecoration: 'underline',
                                            transition: 'color 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.color = '#1565c0';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.color = '#1976d2';
                                          }}
                                          onClick={() => {
                                            const el = document.querySelector(
                                              `[data-question-id="${q.trAssessmentQuestionID}"]`
                                            ) as HTMLElement | null;
                                            if (el) {
                                              el.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'center'
                                              });
                                              el.classList.add('highlight-pulse');
                                              setTimeout(
                                                () => el.classList.remove('highlight-pulse'),
                                                1600
                                              );
                                            }
                                          }}
                                        >
                                          {q.title.length > 120
                                            ? `${q.title.slice(0, 120)}...`
                                            : q.title}
                                        </List.Item>
                                      ))}
                                    </List>
                                  </Stack>
                                </Alert>
                              );
                            })()}
                          </Stack>
                        </Paper>
                      </div>
                    )}

                    {status === 'in progress' && (
                      <Group justify='flex-end' mt='xl'>
                        <Button
                          variant='outline'
                          color='#7B2183'
                          leftSection={
                            draftSaved ? <IconCheck size={16} /> : <IconDeviceFloppy size={16} />
                          }
                          onClick={saveDraft}
                        >
                          {draftSaved ? 'Draft Saved' : 'Save Draft'}
                        </Button>

                        <Button
                          color='#7B2183'
                          leftSection={<IconSend size={16} />}
                          onClick={handleSubmitOrNavigate}
                          loading={submitMutation.isPending}
                          disabled={formSubmitted || submitMutation.isPending}
                        >
                          {submitMutation.isPending
                            ? 'Submitting...'
                            : (() => {
                                // Check if user has answered anything
                                const hasAnyAnswers = (() => {
                                  if (!assessment?.modules) return false;

                                  return assessment.modules.some((module: Module) => {
                                    return module.questions && module.questions.length > 0;
                                  });
                                })();

                                return hasAnyAnswers ? 'Submit Assessment' : 'Share Your Knowledge';
                              })()}
                        </Button>
                      </Group>
                    )}
                  </>
                )}
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card withBorder radius='lg' padding='lg'>
              <Stack gap='md'>
                <Flex gap='xs' align='center'>
                  <IconClock size={20} />
                  <Title order={4}>Timeline</Title>
                </Flex>

                <Group gap='xs' wrap='nowrap'>
                  <IconCalendar size={20} />
                  <Stack gap={0}>
                    <Text size='sm' fw={500}>
                      Start Date
                    </Text>
                    <Text size='sm'>
                      {dayjs.utc(assessment.startTime).local().format('DD/MM/YYYY HH:mm')}
                    </Text>
                  </Stack>
                </Group>

                <Group gap='xs' wrap='nowrap'>
                  <IconCalendar size={20} />
                  <Stack gap={0}>
                    <Text size='sm' fw={500}>
                      End Date
                    </Text>
                    <Text size='sm'>
                      {dayjs.utc(assessment.endTime).local().format('DD/MM/YYYY HH:mm')}
                    </Text>
                  </Stack>
                </Group>

                <div style={{ margin: '12px 0', height: '1px', backgroundColor: '#e0e0e0' }} />

                {/* Assessment Type Info */}
                <Flex gap='xs' align='center'>
                  {assessment.typeAssessment === 'Individu' ? (
                    <IconUser size={20} />
                  ) : (
                    <IconUsers size={20} />
                  )}
                  <Title order={4}>Assessment Type</Title>
                </Flex>

                <Paper p='sm' withBorder>
                  <Group>
                    <Badge
                      color={assessment.typeAssessment === 'Individu' ? 'blue' : 'violet'}
                      size='lg'
                    >
                      {assessment.typeAssessment || 'Team'}
                    </Badge>
                    <Text size='sm'>
                      {assessment.typeAssessment === 'Individu'
                        ? 'Evaluate each participant individually'
                        : 'Evaluate the team as a whole'}
                    </Text>
                  </Group>
                </Paper>

                <div style={{ margin: '12px 0', height: '1px', backgroundColor: '#e0e0e0' }} />

                <Flex gap='xs' align='center'>
                  <IconUser size={20} />
                  <Title order={4}>Your Information</Title>
                </Flex>

                <Paper p='sm' withBorder>
                  <Group>
                    <Flex gap='xs' align='center'>
                      <Avatar color='#7B2183' radius='xl'>
                        {email?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Stack gap={0}>
                        <Text fw={600}>
                          {email.length > 15 ? email.substring(0, 15) + '...' : email}
                        </Text>
                        <Text size='xs' color='dimmed'>
                          External Respondent
                        </Text>
                      </Stack>
                    </Flex>
                  </Group>
                </Paper>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
      </Container>
    </>
  );
};

export default ExternalAssessmentDetailPage;
